import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { useToast } from "@/hooks/use-toast";
import { showInvalidFormFeedback } from "@/lib/invalidFormFeedback";
import { buildContactUpdatePayload } from "@/lib/contactUpdatePayload";
import { apiRequest } from "@/lib/queryClient";
import { CrmRecordAutocomplete } from "@/components/ui/crm-record-autocomplete";
import CompanyModal from "@/components/modals/CompanyModal";
import { RoleAutocomplete } from "@/components/ui/RoleAutocomplete";
import TagInput from "@/components/ui/TagInput";
import { useDebouncedValue } from "@/hooks/useDebounce";
import { Building2, ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle, ExternalLink } from "lucide-react";

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess?: (contact?: any) => void;
  presentation?: 'dialog' | 'sheet';
}

const contactSchema = z.object({
  firstName: z.string().min(1, "Nome obbligatorio"),
  lastName: z.string().min(1, "Cognome obbligatorio"),
  middleName: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  // L'indirizzo è importante per il contatto, ma non blocca la creazione.
  // Quando presente resta soggetto alla validazione completa.
  companyEmail: z.string().trim().email("Email non valida").optional().or(z.literal("")),
  privateEmail: z.string().optional().nullable().or(z.string().email("Email non valida")),
  mobilePhone: z.string().optional().nullable(),
  officePhone: z.string().optional().nullable(),
  privatePhone: z.string().optional().nullable(),
  linkedin: z.string().optional().nullable(),
  facebook: z.string().optional().nullable(),
  instagram: z.string().optional().nullable(),
  tiktok: z.string().optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  notes: z.string().optional().nullable(),
  jobDescription: z.string().optional().nullable(),
});

type ContactFormData = z.infer<typeof contactSchema>;

function formatOriginDate(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function mergeOriginTags(existing: unknown, tag: string): string[] {
  const current = Array.isArray(existing)
    ? existing.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];
  const normalizedTag = tag.trim();
  if (!normalizedTag) return current;

  const seen = new Set(current.map((value) => value.toLowerCase()));
  if (seen.has(normalizedTag.toLowerCase())) return current;
  return [...current, normalizedTag];
}

interface ExtraCompany {
  companyId: number;
  companyName: string;
  role: string;
  isPrimary: boolean;
}

function resolveInitialPrimaryCompany(initialData: any): { id?: number; name: string } {
  const directId = Number(initialData?.companyId);
  if (Number.isSafeInteger(directId) && directId > 0) {
    return { id: directId, name: String(initialData?.companyName || '') };
  }
  const primaryArea = Array.isArray(initialData?.areasOfActivity)
    ? initialData.areasOfActivity.find((area: any) => area?.isPrimary && Number.isSafeInteger(Number(area?.companyId)) && Number(area.companyId) > 0)
    : undefined;
  return primaryArea
    ? { id: Number(primaryArea.companyId), name: String(primaryArea.companyName || '') }
    : { name: '' };
}

export default function ContactModal({ open, onOpenChange, initialData, onSuccess, presentation = 'dialog' }: ContactModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!initialData?.id;
  
  const [location, navigate] = useLocation();
  const hasCompanyContext = location.includes("/companies/");
  const companyIdFromUrl = hasCompanyContext 
    ? parseInt(location.split("/companies/")[1].split("/")[0]) 
    : undefined;

  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get('email');
  const firstNameFromUrl = urlParams.get('firstName');
  const lastNameFromUrl = urlParams.get('lastName');
  const sourceFromUrl = urlParams.get('source');
  const emailMessageIdFromUrl = Number(urlParams.get('emailMessageId'));
  const sourceContext = initialData?.source || sourceFromUrl;
  const emailMessageIdContext = Number(initialData?.emailMessageId || emailMessageIdFromUrl);
  const emailOriginTag = sourceContext === 'email' ? `EMAIL · ${formatOriginDate()}` : null;

  const initialPrimaryCompany = resolveInitialPrimaryCompany(initialData);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(
    initialPrimaryCompany.id?.toString() || (companyIdFromUrl ? companyIdFromUrl.toString() : undefined)
  );
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>(initialPrimaryCompany.name);
  const [isCompanyCreateOpen, setIsCompanyCreateOpen] = useState(false);
  const contactFormRef = useRef<HTMLFormElement>(null);

  const [extraCompanies, setExtraCompanies] = useState<ExtraCompany[]>([]);
  const [newExtraCompany, setNewExtraCompany] = useState<Partial<ExtraCompany>>({ role: "" });
  const [showJobDesc, setShowJobDesc] = useState(false);
  const [showSocial, setShowSocial] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [duplicateConflict, setDuplicateConflict] = useState<any>(null);
  const [submittedAttempt, setSubmittedAttempt] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, dirtyFields } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: initialData?.firstName || firstNameFromUrl || "",
      lastName: initialData?.lastName || lastNameFromUrl || "",
      middleName: initialData?.middleName || "",
      role: initialData?.role || "",
      companyEmail: initialData?.companyEmail || initialData?.sourceEmail || (sourceFromUrl === 'email' ? emailFromUrl : "") || "",
      privateEmail: initialData?.privateEmail || "",
      mobilePhone: initialData?.mobilePhone || "",
      officePhone: initialData?.officePhone || "",
      privatePhone: initialData?.privatePhone || "",
      linkedin: initialData?.linkedin || "",
      facebook: initialData?.facebook || "",
      instagram: initialData?.instagram || "",
      tiktok: initialData?.tiktok || "",
      tags: initialData?.tags || (sourceFromUrl === 'email' ? ['email-import'] : []),
      notes: initialData?.notes || (sourceFromUrl === 'email' ? `Contatto creato da email ricevuta il ${new Date().toLocaleDateString('it-IT')}` : ""),
      jobDescription: initialData?.jobDescription || "",
    }
  });

  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(open, Object.keys(dirtyFields).length > 0, onOpenChange);

  // Reset form quando initialData cambia
  useEffect(() => {
    if (!open) return;
    const resetData = {
      firstName: initialData?.firstName || firstNameFromUrl || "",
      lastName: initialData?.lastName || lastNameFromUrl || "",
      middleName: initialData?.middleName || "",
      role: initialData?.role || "",
      companyEmail: initialData?.companyEmail || initialData?.sourceEmail || (sourceFromUrl === 'email' ? emailFromUrl : "") || "",
      privateEmail: initialData?.privateEmail || "",
      mobilePhone: initialData?.mobilePhone || "",
      officePhone: initialData?.officePhone || "",
      privatePhone: initialData?.privatePhone || "",
      linkedin: initialData?.linkedin || "",
      facebook: initialData?.facebook || "",
      instagram: initialData?.instagram || "",
      tiktok: initialData?.tiktok || "",
      tags: initialData?.tags || (sourceFromUrl === 'email' ? ['email-import'] : []),
      notes: initialData?.notes || (sourceFromUrl === 'email' ? `Contatto creato da email ricevuta il ${new Date().toLocaleDateString('it-IT')}` : ""),
      jobDescription: initialData?.jobDescription || "",
    };
    reset(resetData);
    setSubmittedAttempt(false);
    setDuplicateConflict(null);
    // DialogContent mounts its inputs in a portal. Apply the email-origin
    // fields once the dialog is open so late registration cannot discard them.
    if (!isEditing && sourceContext === 'email') {
      setValue('companyEmail', resetData.companyEmail);
      setValue('role', resetData.role);
      setValue('mobilePhone', resetData.mobilePhone);
      setValue('officePhone', resetData.officePhone);
    }
    
    const tagsFromData = initialData?.tags || (sourceFromUrl === 'email' ? ['email-import'] : []);
    setTagsInput(tagsFromData.length > 0 ? tagsFromData.join(", ") : "");
    
    // Un modale può essere aperto da una scheda, una Stanza o un tab azienda:
    // tutte le origini devono passare dalla stessa relazione primaria.
    const initialCompany = resolveInitialPrimaryCompany(initialData);
    const contextualCompanyId = initialCompany.id || companyIdFromUrl;
    setSelectedCompanyId(contextualCompanyId?.toString());
    setSelectedCompanyName(initialCompany.name);
    
    // Popola extra companies da areasOfActivity (escludendo la primaria)
    const areas = initialData?.areasOfActivity || [];
    const extras = areas
      .filter((a: any) => !a.isPrimary && a.companyId)
      .map((a: any) => ({
        companyId: a.companyId,
        companyName: a.companyName || "Azienda associata",
        role: a.role || "",
        isPrimary: false,
      }));
    setExtraCompanies(extras);
  }, [open, initialData, firstNameFromUrl, lastNameFromUrl, emailFromUrl, sourceFromUrl, reset]);

  // === CONTROLLO DUPLICATI IN TEMPO REALE ===
  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");
  const watchedCompanyEmail = watch("companyEmail");
  const watchedPrivateEmail = watch("privateEmail");
  const debouncedFirstName = useDebouncedValue(watchedFirstName, 400);
  const debouncedLastName = useDebouncedValue(watchedLastName, 400);
  const debouncedCompanyEmail = useDebouncedValue(watchedCompanyEmail || "", 400);
  const debouncedPrivateEmail = useDebouncedValue(watchedPrivateEmail || "", 400);
  const targetCompanyId = selectedCompanyId ? parseInt(selectedCompanyId) : companyIdFromUrl;
  const identityIsSettling = [
    [watchedFirstName, debouncedFirstName],
    [watchedLastName, debouncedLastName],
    [watchedCompanyEmail, debouncedCompanyEmail],
    [watchedPrivateEmail, debouncedPrivateEmail],
  ].some(([current, checked]) => String(current || '').trim() !== String(checked || '').trim());
  const hasIdentityToCheck = Boolean(
    debouncedFirstName?.trim() || debouncedLastName?.trim()
    || debouncedCompanyEmail?.trim() || debouncedPrivateEmail?.trim(),
  );

  const { data: duplicateCheck, isFetching: isCheckingDuplicate, isPending: isDuplicateCheckPending, isError: isDuplicateCheckError } = useQuery({
    queryKey: ["/api/contacts/check-duplicate", debouncedFirstName?.trim(), debouncedLastName?.trim(), debouncedCompanyEmail?.trim(), debouncedPrivateEmail?.trim(), targetCompanyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedFirstName?.trim()) params.append("firstName", debouncedFirstName.trim());
      if (debouncedLastName?.trim()) params.append("lastName", debouncedLastName.trim());
      if (debouncedCompanyEmail?.trim()) params.append("email", debouncedCompanyEmail.trim());
      if (debouncedPrivateEmail?.trim()) params.append("privateEmail", debouncedPrivateEmail.trim());
      if (targetCompanyId) params.append("companyId", targetCompanyId.toString());
      if (isEditing && initialData?.id) params.append("excludeId", String(initialData.id));
      return await apiRequest(`/api/contacts/check-duplicate?${params.toString()}`);
    },
    enabled: open && hasIdentityToCheck && !submittedAttempt,
  });
  const duplicateVerificationPending = identityIsSettling || (hasIdentityToCheck && (isCheckingDuplicate || isDuplicateCheckPending));

  const createContactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const contactData: any = { 
        ...data,
        companyId: selectedCompanyId ? parseInt(selectedCompanyId) : null,
      };
      
      if (tagsInput.trim()) {
        contactData.tags = tagsInput.split(",").map((tag: string) => tag.trim()).filter(Boolean);
      } else {
        contactData.tags = [];
      }

      if (!isEditing && emailOriginTag) {
        contactData.originTags = mergeOriginTags(initialData?.originTags, emailOriginTag);
      }
      
      const newContact = await apiRequest("/api/contacts", { method: "POST", body: JSON.stringify(contactData) });
      // Anche la risposta 202 del recupero post-commit identifica il Contatto
      // come contactId: non saltare firma, participant e associazione Email.
      const createdContactId = Number(newContact.id ?? newContact.contactId);
      const savedContact = { ...newContact, id: createdContactId };

      if (Number.isSafeInteger(createdContactId) && createdContactId > 0
        && Number.isSafeInteger(emailMessageIdContext) && emailMessageIdContext > 0) {
        try {
          await apiRequest('POST', `/api/email/messages/${emailMessageIdContext}/crm-associations`, {
            associations: [{ entityType: 'contact', entityId: createdContactId }],
          });
          await apiRequest('POST', `/api/email/messages/${emailMessageIdContext}/contacts/${createdContactId}/ensure-company`, {});
        } catch (associationError) {
          console.error('Contatto creato ma memoria Email/azienda non completata:', associationError);
          toast({
            title: 'Contatto creato, memoria Email incompleta',
            description: 'Il contatto e stato salvato. Riapri la mail per riprovare collegamento e azienda.',
            variant: 'destructive',
          });
        }
      }

      if (Number.isSafeInteger(createdContactId) && createdContactId > 0) {
        // Crea area di attività primaria
        const targetCompanyId = selectedCompanyId ? parseInt(selectedCompanyId) : companyIdFromUrl;
        if (targetCompanyId) {
          const targetCompanyName = selectedCompanyName;
          await apiRequest("/api/areas-of-activity", {
            method: "POST",
            body: JSON.stringify({
              contactId: createdContactId,
              companyId: targetCompanyId,
              companyName: targetCompanyName,
              role: data.role || '',
              jobDescription: data.jobDescription || '',
              isPrimary: true,
            })
          });
        }

        // Crea aree di attività extra
        for (const extra of extraCompanies) {
          await apiRequest("/api/areas-of-activity", {
            method: "POST",
            body: JSON.stringify({
              contactId: createdContactId,
              companyId: extra.companyId,
              companyName: extra.companyName,
              role: extra.role,
              isPrimary: false,
            })
          });
        }
      }

      return savedContact;
    },
    onSuccess: (contact) => {
      toast({ title: "Contatto creato", description: "Contatto creato con successo" });
      closeAfterSave();
      reset();
      setTagsInput("");
      setExtraCompanies([]);
      setSelectedCompanyId(undefined);
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ['email-contact-match'] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      if (onSuccess) onSuccess(contact);
    },
    onError: (error: any) => {
      const match = error.message?.match(/HTTP 409:\s*(.+)/);
      if (match) {
        try {
          const data = JSON.parse(match[1]);
          const existingContactId = Number(data?.existingContact?.id);
          const duplicateIsExactEmail = /email/i.test(String(data?.reason ?? ''));
          // Nel flusso Email un 409 dopo il preflight indica che il post-ingest
          // ha appena creato lo stesso proprietario. Convergiamo sul record
          // reale, colleghiamo la mail e chiudiamo il form senza falso errore.
          if (!isEditing
            && Number.isSafeInteger(emailMessageIdContext)
            && emailMessageIdContext > 0
            && duplicateIsExactEmail
            && Number.isSafeInteger(existingContactId)
            && existingContactId > 0) {
            void (async () => {
              try {
                await apiRequest('POST', `/api/email/messages/${emailMessageIdContext}/crm-associations`, {
                  associations: [{ entityType: 'contact', entityId: existingContactId }],
                });
                await apiRequest('POST', `/api/email/messages/${emailMessageIdContext}/contacts/${existingContactId}/ensure-company`, {});
              } catch (associationError) {
                console.error('Contatto esistente ma memoria Email/azienda non completata:', associationError);
              }
              toast({ title: 'Contatto già presente', description: 'La mail è stata collegata al contatto esistente.' });
              closeAfterSave();
              queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
              queryClient.invalidateQueries({ queryKey: ['email-contact-match'] });
              queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
              if (onSuccess) onSuccess(data.existingContact);
            })();
            return;
          }
          setDuplicateConflict(data);
          setSubmittedAttempt(false);
          queryClient.invalidateQueries({ queryKey: ['email-contact-match'] });
          return;
        } catch (e) {
          // fallback a toast generico
        }
      }
      setSubmittedAttempt(false);
      toast({ title: "Errore", description: `Errore creazione contatto: ${error.message}`, variant: "destructive" });
    }
  });

  const updateContactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const previousCompanyId = resolveInitialPrimaryCompany(initialData).id ?? null;
      const nextCompanyId = selectedCompanyId ? parseInt(selectedCompanyId) : null;
      const contactData: any = buildContactUpdatePayload(data, previousCompanyId, nextCompanyId, {
        companyEmail: Boolean(dirtyFields.companyEmail),
        role: Boolean(dirtyFields.role),
      });
      
      if (tagsInput.trim()) {
        contactData.tags = tagsInput.split(",").map((tag: string) => tag.trim()).filter(Boolean);
      } else {
        contactData.tags = [];
      }
      
      return await apiRequest(`/api/contacts/${initialData.id}`, { method: "PUT", body: JSON.stringify(contactData) });
    },
    onSuccess: (contact) => {
      toast({ title: "Contatto aggiornato", description: "Contatto aggiornato con successo" });
      closeAfterSave();
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      // Invalida anche la cache del dettaglio contatto per aggiornamento immediato
      if (initialData?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/contacts/${initialData.id}`] });
      }
      if (onSuccess) onSuccess(contact);
    },
    onError: (error: any) => {
      setSubmittedAttempt(false);
      const match = error.message?.match(/HTTP 409:\s*(.+)/);
      if (match) {
        try {
          setDuplicateConflict(JSON.parse(match[1]));
          return;
        } catch {
          // fallback a toast generico
        }
      }
      toast({ title: "Errore", description: `Errore aggiornamento: ${error.message}`, variant: "destructive" });
    }
  });

  // Una lettura realtime puo' ritrovare proprio il contatto appena inserito
  // mentre i collegamenti post-create sono ancora in corso. Non mostrarlo
  // come duplicato finche' la mutation che lo ha creato non e' conclusa.
  const isSaving = createContactMutation.isPending || updateContactMutation.isPending;
  const visibleDuplicateCheck = !isSaving && !submittedAttempt && !identityIsSettling ? duplicateCheck : undefined;

  const onSubmit = (data: ContactFormData) => {
    // Il controllo server e' la barriera finale; non consentire neppure un
    // submit da tastiera mentre la verifica e' in corso o ha trovato un match.
    if (isSaving || duplicateVerificationPending || (isDuplicateCheckError && !isEditing) || visibleDuplicateCheck?.isDuplicate || duplicateConflict) return;
    setSubmittedAttempt(true);
    if (isEditing) {
      updateContactMutation.mutate(data);
    } else {
      createContactMutation.mutate(data);
    }
  };

  const addExtraCompany = () => {
    if (!newExtraCompany.companyId) return;
    // Stessa azienda gia' primaria o gia' in elenco: creerebbe due righe
    // areas_of_activity per la stessa coppia contatto/azienda (bug trovato in
    // audit il 21 lug, stessa famiglia del duplicato partecipanti-deal —
    // qui l'endpoint generico /api/areas-of-activity non aveva alcun
    // controllo). Il picker sotto esclude gia' questi id dalla ricerca;
    // questo e' solo un ulteriore blocco difensivo.
    if (Number(selectedCompanyId) === newExtraCompany.companyId) return;
    if (extraCompanies.some((extra) => extra.companyId === newExtraCompany.companyId)) return;
    setExtraCompanies(prev => [...prev, {
      companyId: newExtraCompany.companyId!,
      companyName: newExtraCompany.companyName || "Azienda associata",
      role: newExtraCompany.role || "",
      isPrimary: false,
    }]);
    setNewExtraCompany({ role: "" });
  };

  const removeExtraCompany = (index: number) => {
    setExtraCompanies(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className={presentation === 'sheet'
        ? "!left-auto !right-0 !top-[env(safe-area-inset-top)] h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-full max-w-2xl !translate-x-0 !translate-y-0 overflow-y-auto rounded-none p-4 [&>*]:min-w-0 sm:p-6"
        : "max-w-2xl overflow-y-auto p-4 [&>*]:min-w-0 sm:w-full sm:p-6"}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-xl font-semibold">
              {isEditing ? "Modifica Contatto" : "Aggiungi Contatto"}
            </DialogTitle>
            {/* Stesso fix del DealModal (audit 21 lug): questo pannello era un
                vicolo cieco, nessun modo di raggiungere la scheda completa
                (email, deal, sinergie, note AI...) da qui. */}
            {isEditing && initialData?.id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1.5 rounded-none text-xs"
                onClick={() => {
                  if (requestClose(false)) navigate(`/contacts/${initialData.id}`);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Apri scheda completa
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <form ref={contactFormRef} onSubmit={handleSubmit(onSubmit, showInvalidFormFeedback)} className="min-w-0 space-y-4 overflow-x-clip">
          {/* === DATI PRINCIPALI === */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Dati Principali</h3>
            
            {/* Nome, Secondo Nome, Cognome */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-sm">Nome *</Label>
                <Input id="firstName" {...register("firstName")} className={errors.firstName ? "border-red-500" : ""} />
                {errors.firstName && <p className="text-red-500 text-xs">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="middleName" className="text-sm">Secondo Nome</Label>
                <Input id="middleName" {...register("middleName")} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-sm">Cognome *</Label>
                <Input id="lastName" {...register("lastName")} placeholder="Da completare" className={errors.lastName ? "border-red-500" : ""} />
                {errors.lastName && <p className="text-red-500 text-xs">{errors.lastName.message}</p>}
              </div>
            </div>

            {/* Ruolo e Azienda */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-sm">Ruolo</Label>
                <RoleAutocomplete
                  value={watch("role") || ""}
                  onChange={(v) => setValue("role", v)}
                  placeholder="Cerca o crea ruolo..."
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Azienda</Label>
                <CrmRecordAutocomplete
                  entityType="company"
                  value={selectedCompanyId || ""}
                  onValueChange={(value) => setSelectedCompanyId(value || undefined)}
                  onOptionSelect={(option) => setSelectedCompanyName(option?.label || "")}
                  placeholder="Cerca azienda..."
                  searchPlaceholder="Cerca in tutte le aziende del CRM..."
                  selectedLabel={selectedCompanyId ? selectedCompanyName : undefined}
                  icon={<Building2 className="h-4 w-4" />}
                />
                {isEditing && selectedCompanyId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-1 h-9 gap-1.5"
                    onClick={() => {
                      setSelectedCompanyId(undefined);
                      setSelectedCompanyName("");
                    }}
                  >
                    Rimuovi azienda
                  </Button>
                )}
                {isEditing && !selectedCompanyId && initialPrimaryCompany.id && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Al salvataggio il rapporto corrente passerà nello storico. Email, Deal, meeting e altre relazioni resteranno conservati.
                  </p>
                )}
                {!selectedCompanyId && (
                  <Button type="button" variant="outline" size="sm" className="mt-1 h-8 gap-1.5"
                    onClick={() => setIsCompanyCreateOpen(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    Crea nuova azienda
                  </Button>
                )}
                {!selectedCompanyId && initialData?.suggestedCompanyName && sourceContext === 'email' && (
                  <p className="text-xs text-muted-foreground">
                    Dominio aziendale rilevato: {initialData.suggestedCompanyName}. Dopo il salvataggio il CRM cerca
                    l'azienda corrispondente e, se il dominio è certo, la crea e la collega al contatto.
                  </p>
                )}
              </div>
            </div>

            {/* Telefoni */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="officePhone" className="text-sm">Telefono Ufficio</Label>
                <Input id="officePhone" {...register("officePhone")} placeholder="+39 02..." />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mobilePhone" className="text-sm">Mobile</Label>
                <Input id="mobilePhone" {...register("mobilePhone")} placeholder="+39 3XX..." />
              </div>
              <div className="space-y-1">
                <Label htmlFor="privatePhone" className="text-sm">Telefono Casa</Label>
                <Input id="privatePhone" {...register("privatePhone")} placeholder="+39 02..." />
              </div>
            </div>

            {isEditing && <div className="space-y-2 border border-border p-3 text-sm">
              <p>La persona può avere più email di lavoro o private. Cambiare il testo conserva il vecchio indirizzo e la corrispondenza; non indica un cambio di lavoro.</p>
              <Button type="button" variant="outline" disabled={Object.keys(dirtyFields).length > 0} onClick={() => { if (requestClose(false)) navigate(`/contacts/${initialData.id}?tab=emails`); }}>Gestisci indirizzi e email principale</Button>
              <p className="text-xs text-muted-foreground">Salva prima con Aggiorna le eventuali modifiche di questo modulo.</p>
            </div>}
            {/* Email */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="companyEmail" className="text-sm">
                  Email aziendale <span className="ml-1 text-xs font-medium text-amber-700">Importante</span>
                </Label>
                <Input id="companyEmail" type="email" {...register("companyEmail")} className={errors.companyEmail ? "border-red-500" : ""} />
                {errors.companyEmail && <p className="text-red-500 text-xs">{errors.companyEmail.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="privateEmail" className="text-sm">Email Privata</Label>
                <Input id="privateEmail" type="email" {...register("privateEmail")} />
                {errors.privateEmail && <p className="text-red-500 text-xs">{errors.privateEmail.message}</p>}
              </div>
            </div>
          </div>

          {/* === WARNING DUPLICATO === */}
          {isDuplicateCheckError && !isSaving && !submittedAttempt && (
            <p className="text-sm text-red-700" role="alert">
              {isEditing
                ? "Verifica duplicati non disponibile; l'aggiornamento sarà controllato dal server."
                : "Verifica duplicati non disponibile. Riprova tra poco."}
            </p>
          )}
          {visibleDuplicateCheck?.isDuplicate && (
            <div className="rounded-none border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" />
                Contatto già presente nel CRM
              </div>
              <div className="mt-1">
                {visibleDuplicateCheck.reason}.{" "}
                <a
                  href={`/contacts/${visibleDuplicateCheck.existingContact.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium hover:text-red-900"
                >
                  Vedi {visibleDuplicateCheck.existingContact.firstName} {visibleDuplicateCheck.existingContact.lastName}
                </a>
              </div>
            </div>
          )}

          {/* === JOB DESCRIPTION (collassabile) === */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowJobDesc(!showJobDesc)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {showJobDesc ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Job Description
            </button>
            {showJobDesc && (
              <div className="mt-2">
                <VoiceAssistantTextarea
                  value={watch("jobDescription") || ""}
                  onValueChange={(value) => setValue("jobDescription", value, { shouldDirty: true })}
                  fieldLabel="Job Description Contatto"
                  martinaContext={{ terminalType: "entity", entityType: "contact", entityId: initialData?.id ? String(initialData.id) : undefined, route: initialData?.id ? `/contacts/${initialData.id}` : "/contacts" }}
                  placeholder="Descrizione del ruolo, responsabilità, competenze..."
                  rows={4}
                />
              </div>
            )}
          </div>

          {/* === ALTRE AZIENDE (Aree di Attività) === */}
          <div className="border-t pt-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Altre Aziende</h3>
            
            {/* Lista aziende extra */}
            {extraCompanies.length > 0 && (
              <div className="space-y-2 mb-3">
                {extraCompanies.map((extra, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-muted/50 border">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-sm font-medium">{extra.companyName}</span>
                    {extra.role && <span className="text-xs text-muted-foreground">({extra.role})</span>}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExtraCompany(idx)}
                      className="h-6 w-6 p-0 text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Aggiungi nuova azienda */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <CrmRecordAutocomplete
                  entityType="company"
                  value={newExtraCompany.companyId?.toString() || ""}
                  onValueChange={(value) => setNewExtraCompany(prev => ({ ...prev, companyId: value ? parseInt(value) : undefined }))}
                  onOptionSelect={(option) => setNewExtraCompany(prev => ({ ...prev, companyName: option?.label || "" }))}
                  placeholder="Cerca azienda..."
                  searchPlaceholder="Cerca in tutte le aziende del CRM..."
                  selectedLabel={newExtraCompany.companyName}
                  icon={<Building2 className="h-4 w-4" />}
                  excludeIds={[
                    ...(selectedCompanyId ? [selectedCompanyId] : []),
                    ...extraCompanies.map((extra) => extra.companyId),
                  ]}
                />
              </div>
              <div className="w-40">
                <RoleAutocomplete
                  value={newExtraCompany.role || ""}
                  onChange={(v) => setNewExtraCompany(prev => ({ ...prev, role: v }))}
                  placeholder="Ruolo..."
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addExtraCompany}
                disabled={!newExtraCompany.companyId}
              >
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi
              </Button>
            </div>
          </div>

          {/* === SOCIAL MEDIA (collassabile) === */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowSocial(!showSocial)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {showSocial ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Social Media
            </button>
            {showSocial && (
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="linkedin" className="text-sm">LinkedIn</Label>
                  <Input id="linkedin" {...register("linkedin")} placeholder="linkedin.com/in/..." />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="facebook" className="text-sm">Facebook</Label>
                  <Input id="facebook" {...register("facebook")} placeholder="facebook.com/..." />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instagram" className="text-sm">Instagram</Label>
                  <Input id="instagram" {...register("instagram")} placeholder="instagram.com/..." />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="tiktok" className="text-sm">TikTok</Label>
                  <Input id="tiktok" {...register("tiktok")} placeholder="tiktok.com/@..." />
                </div>
              </div>
            )}
          </div>

          {/* === TAGS === */}
          <div className="border-t pt-3">
            <Label className="text-sm">Tags</Label>
            <TagInput
              value={tagsInput.split(",").map(t => t.trim()).filter(Boolean)}
              onChange={(tags) => setTagsInput(tags.join(", "))}
              placeholder="Aggiungi tag..."
            />
          </div>

          {/* === NOTE === */}
          <div className="space-y-1">
            <Label htmlFor="notes" className="text-sm">Note</Label>
            <VoiceAssistantTextarea
              id="notes"
              value={watch("notes") || ""}
              onValueChange={(value) => setValue("notes", value, { shouldDirty: true })}
              fieldLabel="Note Contatto"
              martinaContext={{ terminalType: "entity", entityType: "contact", entityId: initialData?.id ? String(initialData.id) : undefined, route: initialData?.id ? `/contacts/${initialData.id}` : "/contacts" }}
              placeholder="Note aggiuntive sul contatto..."
              rows={3}
            />
          </div>
          
          {duplicateConflict && (
            <div className="rounded-none border border-amber-200 bg-amber-50 p-4 text-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900">Contatto già esistente</p>
                  <p className="text-amber-800 mt-1">{duplicateConflict.reason}</p>
                  <div className="mt-2 p-2 bg-white border border-amber-200">
                    <p className="font-medium text-foreground">
                      {duplicateConflict.existingContact?.firstName} {duplicateConflict.existingContact?.lastName}
                    </p>
                    {duplicateConflict.existingContact?.companyName && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {duplicateConflict.existingContact.companyName}
                      </p>
                    )}
                    {duplicateConflict.existingContact?.role && (
                      <p className="text-xs text-muted-foreground">{duplicateConflict.existingContact.role}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`/contacts/${duplicateConflict.existingContact.id}`, '_blank');
                      }}
                    >
                      Apri contatto
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDuplicateConflict(null)}
                    >
                      Chiudi
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-background px-4 py-3 sm:-mx-6 sm:px-6">
            <Button type="button" variant="outline" onClick={() => requestClose(false)}>
              Annulla
            </Button>
            <Button 
              type="submit"
              disabled={isSaving || duplicateVerificationPending || (isDuplicateCheckError && !isEditing) || !!visibleDuplicateCheck?.isDuplicate || !!duplicateConflict}
            >
              {isSaving ? "Salvataggio..." : (duplicateVerificationPending ? "Verifica contatto..." : (isEditing ? "Aggiorna" : "Crea Contatto"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <CompanyModal
        open={isCompanyCreateOpen}
        onOpenChange={(nextOpen) => {
          setIsCompanyCreateOpen(nextOpen);
          if (!nextOpen) {
            // Radix ripristina il focus sul trigger e puo' far scorrere il form
            // anche con overflow-x-hidden: il rientro deve restare leggibile.
            requestAnimationFrame(() => {
              if (contactFormRef.current) contactFormRef.current.scrollLeft = 0;
            });
          }
        }}
        mode="create"
        onSuccess={(company) => {
          const companyId = Number(company?.id);
          if (!Number.isSafeInteger(companyId) || companyId <= 0) return;
          setSelectedCompanyId(String(companyId));
          setSelectedCompanyName(company?.name || '');
          setIsCompanyCreateOpen(false);
        }}
      />
    </Dialog>
  );
}
